/**
 * QZ Tray Direct Printing Service
 * Enables silent/direct printing to Windows/USB printers without print dialog
 * 
 * Requirements:
 * 1. QZ Tray must be installed on the client machine: https://qz.io/download/
 * 2. Certificate is configured for trusted/silent printing
 */

import qz from 'qz-tray';

// Connection state
let isConnected = false;
let connectionPromise = null;
let securityConfigured = false;

// API URL for signing requests
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Configure QZ Tray security for trusted/silent printing
 * This prevents the security pop-ups by signing requests
 */
const configureQZSecurity = async () => {
  if (securityConfigured) return;
  
  try {
    // Set signature algorithm to SHA512 (QZ Tray 2.1+ default)
    qz.security.setSignatureAlgorithm("SHA512");
    
    // Set up certificate promise - fetches the trusted certificate from our backend
    qz.security.setCertificatePromise((resolve, reject) => {
      fetch(`${API_URL}/api/qz/certificate`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
        .then(response => response.json())
        .then(data => {
          if (data.success && data.certificate) {
            resolve(data.certificate);
          } else {
            // Return empty to allow unsigned connections (will show prompts)
            console.warn('[QZ Tray] No certificate configured, prompts will appear');
            resolve('');
          }
        })
        .catch(err => {
          console.warn('[QZ Tray] Certificate fetch failed:', err.message);
          resolve(''); // Allow connection without certificate
        });
    });

    // Set up signature promise - signs each request with our private key
    qz.security.setSignaturePromise((toSign) => {
      return (resolve, reject) => {
        fetch(`${API_URL}/api/qz/sign`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ request: toSign })
        })
          .then(response => response.json())
          .then(data => {
            if (data.success && data.signature) {
              resolve(data.signature);
            } else {
              console.warn('[QZ Tray] Signing failed, prompts may appear');
              resolve(''); // Allow without signature
            }
          })
          .catch(err => {
            console.warn('[QZ Tray] Signing request failed:', err.message);
            resolve(''); // Allow without signature
          });
      };
    });

    securityConfigured = true;
    console.log('[QZ Tray] Security configured for trusted printing (SHA512)');
  } catch (error) {
    console.warn('[QZ Tray] Security configuration failed:', error.message);
  }
};

/**
 * Connect to QZ Tray
 * @returns {Promise<boolean>} - true if connected successfully
 */
export const connectQZ = async () => {
  if (isConnected && qz.websocket.isActive()) {
    return true;
  }

  // Prevent multiple simultaneous connection attempts
  if (connectionPromise) {
    return connectionPromise;
  }

  connectionPromise = new Promise(async (resolve) => {
    try {
      // Check if already connected
      if (qz.websocket.isActive()) {
        isConnected = true;
        resolve(true);
        return;
      }

      // Configure security for trusted/silent printing
      await configureQZSecurity();
      
      // Connect to QZ Tray
      await qz.websocket.connect();
      isConnected = true;
      console.log('[QZ Tray] Connected successfully with trusted certificate');
      resolve(true);
    } catch (error) {
      console.warn('[QZ Tray] Connection failed:', error.message);
      isConnected = false;
      resolve(false);
    } finally {
      connectionPromise = null;
    }
  });

  return connectionPromise;
};

/**
 * Disconnect from QZ Tray
 */
export const disconnectQZ = async () => {
  try {
    if (qz.websocket.isActive()) {
      await qz.websocket.disconnect();
    }
    isConnected = false;
  } catch (error) {
    console.warn('[QZ Tray] Disconnect error:', error);
  }
};

/**
 * Check if QZ Tray is available and connected
 * @returns {boolean}
 */
export const isQZConnected = () => {
  return isConnected && qz.websocket.isActive();
};

/**
 * Check if QZ Tray is installed (attempts connection)
 * @returns {Promise<boolean>}
 */
export const isQZAvailable = async () => {
  try {
    const connected = await connectQZ();
    return connected;
  } catch {
    return false;
  }
};

/**
 * Get list of available printers from Windows
 * @returns {Promise<string[]>} - Array of printer names
 */
export const getWindowsPrinters = async () => {
  try {
    const connected = await connectQZ();
    if (!connected) {
      console.warn('[QZ Tray] Not connected, cannot get printers');
      return [];
    }

    const printers = await qz.printers.find();
    console.log('[QZ Tray] Found printers:', printers);
    return printers;
  } catch (error) {
    console.error('[QZ Tray] Error getting printers:', error);
    return [];
  }
};

/**
 * Get the default printer
 * @returns {Promise<string|null>}
 */
export const getDefaultPrinter = async () => {
  try {
    const connected = await connectQZ();
    if (!connected) return null;

    const defaultPrinter = await qz.printers.getDefault();
    return defaultPrinter;
  } catch (error) {
    console.error('[QZ Tray] Error getting default printer:', error);
    return null;
  }
};

/**
 * Print raw ESC/POS commands directly to a thermal printer
 * @param {string} printerName - Exact printer name from Windows
 * @param {string[]} commands - Array of ESC/POS commands
 * @returns {Promise<boolean>}
 */
export const printRaw = async (printerName, commands) => {
  try {
    const connected = await connectQZ();
    if (!connected) {
      throw new Error('QZ Tray not connected');
    }

    const config = qz.configs.create(printerName);
    await qz.print(config, commands);
    console.log('[QZ Tray] Raw print successful to:', printerName);
    return true;
  } catch (error) {
    console.error('[QZ Tray] Raw print error:', error);
    throw error;
  }
};

/**
 * Print HTML content directly to a printer (rendered)
 * @param {string} printerName - Exact printer name from Windows
 * @param {string} htmlContent - HTML string to print
 * @param {object} options - Print options
 * @returns {Promise<boolean>}
 */
export const printHTML = async (printerName, htmlContent, options = {}) => {
  try {
    const connected = await connectQZ();
    if (!connected) {
      throw new Error('QZ Tray not connected. Please ensure QZ Tray is installed and running.');
    }

    const config = qz.configs.create(printerName, {
      margins: options.margins || { top: 0, right: 0, bottom: 0, left: 0 },
      units: 'mm',
      scaleContent: true,
      rasterize: true,  // Convert to image for thermal printers
      ...options
    });

    const data = [{
      type: 'html',
      format: 'plain',
      data: htmlContent
    }];

    await qz.print(config, data);
    console.log('[QZ Tray] HTML print successful to:', printerName);
    return true;
  } catch (error) {
    console.error('[QZ Tray] HTML print error:', error);
    throw error;
  }
};

/**
 * Print a receipt directly to a thermal printer using HTML rendering
 * @param {string} printerName - Printer name
 * @param {object} receiptData - Receipt data
 * @param {string} paperSize - '80mm' or '58mm'
 * @returns {Promise<boolean>}
 */
export const printReceipt = async (printerName, receiptData, paperSize = '80mm') => {
  console.log('[QZ Tray] printReceipt called with paperSize:', paperSize);
  
  // Generate HTML for the receipt using the same format as browser printing
  const htmlContent = generateReceiptHTMLForQZ(receiptData, paperSize);
  
  try {
    const connected = await connectQZ();
    if (!connected) {
      throw new Error('QZ Tray not connected');
    }

    // Use pixel-based configuration for proper thermal printer rendering
    const is58mm = paperSize === '58mm';
    const pageWidth = is58mm ? 48 : 72; // mm
    
    const config = qz.configs.create(printerName, {
      units: 'mm',
      size: { width: pageWidth, height: null }, // Auto height
      margins: { top: 0, right: 0, bottom: 0, left: 0 },
      colorType: 'grayscale',
      interpolation: 'nearest-neighbor',
      scaleContent: true,
      rasterize: true  // Convert to image for thermal printers
    });

    const data = [{
      type: 'html',
      format: 'plain',
      data: htmlContent
    }];

    await qz.print(config, data);
    console.log('[QZ Tray] Receipt printed to:', printerName);
    return true;
  } catch (error) {
    console.error('[QZ Tray] Receipt print error:', error);
    throw error;
  }
};


/**
 * Generate HTML for receipt printing via QZ Tray
 * Matches the format used in print-utils.js for consistency
 * @param {Object} data - Receipt data
 * @param {string} paperSize - '80mm' or '58mm'
 * @returns {string} HTML content
 */
const generateReceiptHTMLForQZ = (data, paperSize = '80mm') => {
  const is58mm = paperSize === '58mm';
  const pageWidth = is58mm ? '48mm' : '72mm';
  const bodyWidth = is58mm ? '44mm' : '68mm';
  const fontSize = is58mm ? '10px' : '12px';
  const smallFont = is58mm ? '8px' : '10px';
  const template = data.template || {};
  const labels = template.labels || {};
  
  // Use template font_family or default to a bold, clear font for thermal printing
  const fontFamily = template.font_family || 'Arial';

  // Format currency with 2 decimal places
  const formatCurrency = (num) => (num || 0).toFixed(2);

  // Render items
  const itemsHtml = (data.items || []).map(item => {
    const price = ((item.unit_price || item.price || 0) * (item.quantity || 1)).toFixed(2);
    return `<div class="item-row"><span>${item.quantity}x ${item.name}</span><span>${price}</span></div>`;
  }).join('');

  // Tax breakdown
  let taxHtml = '';
  if (template.show_detailed_taxes !== false && data.taxBreakdown?.length > 0) {
    taxHtml = data.taxBreakdown.map(t => 
      `<div class="item-row tax-line"><span>${t.name} (${t.rate}%)</span><span>${formatCurrency(t.amount)}</span></div>`
    ).join('');
  } else if (data.tax) {
    taxHtml = `<div class="item-row"><span>${labels.tax || 'Tax'}</span><span>${formatCurrency(data.tax)}</span></div>`;
  }

  // eTIMS section
  let etimsHtml = '';
  if (data.etims?.submitted) {
    etimsHtml = `
      <div class="divider"></div>
      <div class="center bold">eTIMS FISCAL RECEIPT</div>
      <div class="center small">CU Serial: ${data.etims.sdc_id || ''}</div>
      <div class="center small">eTIMS Invoice #: ${data.etims.invc_no || ''}</div>
      <div class="center small">Receipt Sign: ${data.etims.rcpt_sign || ''}</div>
      ${data.kraPin ? `<div class="center small">TIN: ${data.kraPin}</div>` : ''}
      ${data.etims.qr_base64 ? `
        <div class="center qr-container">
          <img src="data:image/png;base64,${data.etims.qr_base64}" class="qr-code" alt="eTIMS QR"/>
        </div>
        <div class="center" style="font-size:8px;">Scan to verify on KRA eTIMS portal</div>
      ` : ''}
    `;
  }

  // Reprint indicator
  const reprintHtml = (data.isReprint || data.printCount > 1) 
    ? `<div class="center bold reprint">*** REPRINT ${(data.printCount || 2) - 1} ***</div>` 
    : '';

  // Template header/footer items
  const headerHtml = (template.header_items || []).map(item => 
    `<div class="center small">${item.type === 'text' ? item.text : `${item.key}: ${item.value}`}</div>`
  ).join('');
  
  const footerHtml = (template.footer_items || []).length > 0
    ? (template.footer_items || []).map(item => 
        `<div class="center small">${item.type === 'text' ? item.text : `${item.key}: ${item.value}`}</div>`
      ).join('')
    : '<div class="center small">Thank you for your business!</div>';

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page { margin: 0; size: ${pageWidth} auto; }
    * { margin: 0; padding: 0; box-sizing: border-box; -webkit-print-color-adjust: exact; }
    body { 
      font-family: '${fontFamily}', 'Arial', sans-serif; 
      font-size: ${fontSize}; 
      font-weight: 500;
      width: ${bodyWidth}; 
      margin: 0 auto; 
      padding: 2mm;
      line-height: 1.4;
      color: #000;
      -webkit-font-smoothing: antialiased;
    }
    .center { text-align: center; }
    .bold { font-weight: 700; }
    .small { font-size: ${smallFont}; font-weight: 500; }
    .divider { border-top: 1px dashed #000; margin: 2mm 0; }
    .item-row { 
      display: flex; 
      justify-content: space-between; 
      margin: 1mm 0;
      font-weight: 500;
    }
    .item-row span:last-child { text-align: right; min-width: 50px; }
    .total-row { 
      display: flex; 
      justify-content: space-between; 
      font-weight: 700; 
      font-size: ${is58mm ? '13px' : '15px'}; 
      margin: 1mm 0;
    }
    .tax-line { font-size: ${smallFont}; font-weight: 500; }
    .qr-container { margin: 3mm 0; }
    .qr-code { width: 100px; height: 100px; }
    .reprint { margin-top: 2mm; font-weight: 700; }
    .business-name { font-size: ${is58mm ? '13px' : '15px'}; font-weight: 700; }
  </style>
</head>
<body>
  ${template.logo_url ? `<div class="center"><img src="${template.logo_url}" style="max-width:${is58mm ? '35mm' : '50mm'};max-height:${is58mm ? '15mm' : '20mm'};" alt="Logo"/></div>` : ''}
  <div class="center business-name">${data.businessName || ''}</div>
  <div class="center small">${data.businessAddress || ''}</div>
  ${data.businessPhone ? `<div class="center small">Tel: ${data.businessPhone}</div>` : ''}
  ${data.kraPin ? `<div class="center small">PIN: ${data.kraPin}</div>` : ''}
  ${headerHtml}
  <div class="divider"></div>
  <div class="small">${labels.invoice_number || 'Inv#'}: ${data.invoiceNumber || ''}</div>
  <div class="small">${labels.date || 'Date'}: ${new Date().toLocaleString()}</div>
  ${data.tableNumber ? `<div class="small">${labels.table || 'Table'}: ${data.tableNumber}</div>` : ''}
  ${data.waiterName ? `<div class="small">${labels.server || 'Server'}: ${data.waiterName}</div>` : ''}
  ${data.customerName && data.customerName !== 'Walk-in' ? `<div class="small">${labels.customer || 'Customer'}: ${data.customerName}</div>` : ''}
  <div class="divider"></div>
  ${itemsHtml}
  <div class="divider"></div>
  ${data.subtotal ? `<div class="item-row"><span>${labels.subtotal || 'Subtotal'}</span><span>${formatCurrency(data.subtotal)}</span></div>` : ''}
  ${taxHtml}
  ${data.discount ? `<div class="item-row"><span>${labels.discount || 'Discount'}</span><span>-${formatCurrency(data.discount)}</span></div>` : ''}
  <div class="total-row"><span>${labels.total || 'TOTAL'}</span><span>KES ${formatCurrency(data.total)}</span></div>
  ${data.paymentMethod ? `<div class="item-row"><span>Paid (${(data.paymentMethod || '').toUpperCase()})</span><span>KES ${formatCurrency(data.amountPaid || data.total)}</span></div>` : ''}
  ${data.changeAmount > 0 ? `<div class="item-row"><span>Change</span><span>KES ${formatCurrency(data.changeAmount)}</span></div>` : ''}
  ${etimsHtml}
  <div class="divider"></div>
  ${footerHtml}
  ${reprintHtml}
  <div style="height:5mm;"></div>
</body>
</html>`;
};


/**
 * Generate ESC/POS commands for a receipt (BACKUP - kept for reference)
 * @param {Object} data - Receipt data including template settings
 * @param {boolean} is58mm - Whether to format for 58mm paper
 */
const generateReceiptCommands = (data, is58mm = false) => {
  // 58mm = 32 chars, 80mm = 48 chars per line (standard thermal printer)
  const lineWidth = is58mm ? 32 : 48;
  const nameWidth = is58mm ? 20 : 34;  // Name column width
  const priceWidth = is58mm ? 12 : 14; // Price column width (nameWidth + priceWidth = lineWidth)
  const divider = is58mm ? '--------------------------------' : '------------------------------------------------';
  
  console.log('[QZ Tray] Receipt format - lineWidth:', lineWidth, 'nameWidth:', nameWidth, 'priceWidth:', priceWidth, 'is58mm:', is58mm);
  const cmds = [];
  const template = data.template || {};
  const headerItems = template.header_items || [];
  const footerItems = template.footer_items || [];
  
  // Initialize printer
  cmds.push('\x1B\x40'); // ESC @ - Initialize
  
  // Set print area width for 80mm paper (576 dots at 203dpi)
  // GS W - Set print area width: nL nH (576 = 0x240 → nL=0x40, nH=0x02)
  if (!is58mm) {
    cmds.push('\x1D\x57\x40\x02'); // 576 dots width for 80mm paper
  } else {
    cmds.push('\x1D\x57\x80\x01'); // 384 dots width for 58mm paper
  }
  
  cmds.push('\x1B\x61\x01'); // Center align
  
  // Business name (double height)
  cmds.push('\x1B\x21\x10'); // Double height
  cmds.push((data.businessName || 'Restaurant') + '\n');
  cmds.push('\x1B\x21\x00'); // Normal
  
  // Address and contact
  if (data.businessAddress) {
    cmds.push(data.businessAddress + '\n');
  }
  if (data.businessPhone) {
    cmds.push('Tel: ' + data.businessPhone + '\n');
  }
  
  // Template header items (from configured template)
  if (headerItems.length > 0) {
    headerItems.forEach(item => {
      if (item.type === 'text' && item.text) {
        cmds.push(item.text + '\n');
      } else if (item.key && item.value) {
        cmds.push(item.key + ': ' + item.value + '\n');
      }
    });
  }
  
  cmds.push(divider + '\n');
  
  // Receipt header
  cmds.push('\x1B\x21\x08'); // Bold
  cmds.push('RECEIPT\n');
  cmds.push('\x1B\x21\x00'); // Normal
  
  cmds.push('\x1B\x61\x00'); // Left align
  cmds.push('Inv#: ' + (data.invoiceNumber || '') + '\n');
  cmds.push('Date: ' + new Date().toLocaleString() + '\n');
  if (data.tableNumber) cmds.push('Table: ' + data.tableNumber + '\n');
  if (data.waiterName) cmds.push('Server: ' + data.waiterName + '\n');
  if (data.customerName && data.customerName !== 'Walk-in') {
    cmds.push('Customer: ' + data.customerName + '\n');
  }
  
  cmds.push(divider + '\n');
  
  // Items
  (data.items || []).forEach(item => {
    const qty = item.quantity || 1;
    const price = (qty * (item.price || item.unit_price || 0)).toFixed(2);
    const name = item.name || '';
    const line = `${qty}x ${name}`;
    // Right-pad name, right-align price
    cmds.push(line.substring(0, nameWidth).padEnd(nameWidth) + price.padStart(priceWidth) + '\n');
  });
  
  cmds.push(divider + '\n');
  
  // Totals
  if (data.subtotal) {
    cmds.push('Subtotal'.padEnd(nameWidth) + data.subtotal.toFixed(2).padStart(priceWidth) + '\n');
  }
  
  // Tax breakdown
  if (data.showDetailedTaxes && data.taxBreakdown?.length > 0) {
    data.taxBreakdown.forEach(tax => {
      const label = `${tax.name} (${tax.rate}%)`;
      cmds.push(label.padEnd(nameWidth) + (tax.amount || 0).toFixed(2).padStart(priceWidth) + '\n');
    });
  } else if (data.tax) {
    cmds.push('Tax'.padEnd(nameWidth) + data.tax.toFixed(2).padStart(priceWidth) + '\n');
  }
  
  if (data.discount > 0) {
    cmds.push('Discount'.padEnd(nameWidth) + ('-' + data.discount.toFixed(2)).padStart(priceWidth) + '\n');
  }
  
  // Total (bold, double height)
  cmds.push('\x1B\x21\x18'); // Bold + Double height
  cmds.push('TOTAL'.padEnd(nameWidth) + ('KES ' + (data.total || 0).toFixed(2)).padStart(priceWidth) + '\n');
  cmds.push('\x1B\x21\x00'); // Normal
  
  // Payment info
  if (data.paymentMethod) {
    cmds.push(('Paid (' + data.paymentMethod.toUpperCase() + ')').padEnd(nameWidth) + ('KES ' + (data.amountPaid || data.total || 0).toFixed(2)).padStart(priceWidth) + '\n');
  }
  if (data.changeAmount && data.changeAmount > 0) {
    cmds.push('Change'.padEnd(nameWidth) + ('KES ' + data.changeAmount.toFixed(2)).padStart(priceWidth) + '\n');
  }
  
  cmds.push(divider + '\n');
  
  // Reprint indicator
  if (data.isReprint || data.printCount > 1) {
    cmds.push('\x1B\x61\x01'); // Center
    cmds.push('\x1B\x21\x08'); // Bold
    cmds.push('*** REPRINT ' + ((data.printCount || 2) - 1) + ' ***\n');
    cmds.push('\x1B\x21\x00'); // Normal
  }
  
  // eTIMS info if available
  if (data.etims?.submitted) {
    cmds.push(divider + '\n');
    cmds.push('\x1B\x21\x08'); // Bold
    cmds.push('eTIMS FISCAL RECEIPT\n');
    cmds.push('\x1B\x21\x00'); // Normal
    cmds.push('CU: ' + (data.etims.sdc_id || '') + '\n');
    if (data.etims.invc_no) cmds.push('eTIMS#: ' + data.etims.invc_no + '\n');
    if (data.etims.rcpt_sign) cmds.push('Sign: ' + data.etims.rcpt_sign + '\n');
    if (data.kraPin) cmds.push('TIN: ' + data.kraPin + '\n');
    
    // Print QR code if available
    if (data.etims.qr_data) {
      cmds.push('\x1B\x61\x01'); // Center align for QR code
      // QR Code: GS ( k - set QR code model, size, error correction, store data, print
      const qrData = data.etims.qr_data;
      const qrLen = qrData.length + 3;
      const pL = qrLen % 256;
      const pH = Math.floor(qrLen / 256);
      
      // Set QR model
      cmds.push('\x1D\x28\x6B\x04\x00\x31\x41\x32\x00');
      // Set QR size (module size 4)
      cmds.push('\x1D\x28\x6B\x03\x00\x31\x43\x04');
      // Set error correction level (L)
      cmds.push('\x1D\x28\x6B\x03\x00\x31\x45\x30');
      // Store QR data
      cmds.push('\x1D\x28\x6B' + String.fromCharCode(pL) + String.fromCharCode(pH) + '\x31\x50\x30' + qrData);
      // Print QR code
      cmds.push('\x1D\x28\x6B\x03\x00\x31\x51\x30');
      cmds.push('\n');
    }
  }
  
  cmds.push(divider + '\n');
  
  // Template footer items (from configured template)
  cmds.push('\x1B\x61\x01'); // Center align
  if (footerItems.length > 0) {
    footerItems.forEach(item => {
      if (item.type === 'text' && item.text) {
        cmds.push(item.text + '\n');
      } else if (item.key && item.value) {
        cmds.push(item.key + ': ' + item.value + '\n');
      }
    });
  } else {
    // Default footer if no template items
    cmds.push('Thank you for your business!\n');
  }
  
  // Feed and cut
  cmds.push('\n\n\n');
  cmds.push('\x1D\x56\x42\x03'); // Partial cut
  
  return cmds;
};


// Export default object with all functions
export default {
  connect: connectQZ,
  disconnect: disconnectQZ,
  isConnected: isQZConnected,
  isAvailable: isQZAvailable,
  getPrinters: getWindowsPrinters,
  getDefaultPrinter,
  printRaw,
  printHTML,
  printReceipt
};
